import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PackageCheck, Send, Undo2, XCircle } from 'lucide-react'
import { closePO, getPO, getPORemaining, listReceiptsForPO, normalizePOApproval, scanPOApproval, submitPOForApproval } from '../../api/purchase'
import { useToast } from '../../components/Toast.jsx'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money, num } from '../sales/salesShared'
import { poState } from './purchaseShared'
import { MaterialTag, useMaterialIds } from './itemFor'

export default function PODetail() {
  const materialIds = useMaterialIds()
  const { poId } = useParams()
  const { notify } = useToast()
  const { roles } = useSession()
  const [hdr, setHdr] = useState(null)
  const [lines, setLines] = useState([])
  const [costs, setCosts] = useState([])
  const [totals, setTotals] = useState(null)
  const [remaining, setRemaining] = useState([])
  const [receipts, setReceipts] = useState([])
  const [check, setCheck] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')

  const load = async () => {
    try {
      const data = await getPO(poId)
      setHdr(data.header || null)
      setLines(data.lines || [])
      setCosts(data.additional_costs || [])
      setTotals(data.totals || null)
    } catch (e) {
      setHdr(null)
      setError(e.message || 'Could not load this order.')
    }
    getPORemaining(poId, { net: false }).then((r) => setRemaining(r || [])).catch(() => setRemaining([]))
    listReceiptsForPO(poId).then((r) => setReceipts(r || [])).catch(() => setReceipts([]))
    scanPOApproval(poId).then(setCheck).catch(() => setCheck(null))
  }

  useEffect(() => { load().finally(() => setLoading(false)) }, [poId])

  const run = async (kind, fn, message) => {
    setError('')
    setBusy(kind)
    try {
      const r = await fn()
      notify(typeof message === 'function' ? message(r) : message)
      await load()
    } catch (e) {
      setError(e.message || 'That did not work. Please try again.')
    } finally {
      setBusy('')
    }
  }

  const st = hdr ? poState(hdr) : null
  const approval = String(hdr?.approval_status || '').toLowerCase()
  const overLimit = check && num(check.threshold) > 0 && num(check.effective_total) >= num(check.threshold)
  const canSubmit = hdr && overLimit && !['pending', 'approved'].includes(approval) && st?.id !== 'closed'
  const canReceive = st?.id === 'ordered'
  const canClose = hdr && st?.id !== 'closed' && st?.id !== 'rejected'
  const stillToCome = remaining.filter((r) => num(r.remaining) > 0)
  const lineTotal = lines.reduce((s, l) => s + num(l.quantity) * num(l.unit_cost), 0)

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/purchase/po-list" label="Orders" />}
        title={docNo('po', hdr?.po_no) || 'Purchase order'}
        badge={st && <StatusBadge label={st.label} tone={st.tone} />}
        meta={hdr && (
          <>
            {hdr.vendor_id ? <Link to={`/purchase/ap/vendor/${hdr.vendor_id}`}>{hdr.vendor_name || `Vendor #${hdr.vendor_id}`}</Link> : hdr.vendor_name}
            {hdr.po_date && ` · ordered ${fmtDay(hdr.po_date)}`}
          </>
        )}
        actions={hdr && (
          <>
            {canClose && (
              <button type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={() => window.confirm(`Close ${docNo('po', hdr.po_no)}? Nothing more can be received against it.`) && run('close', () => closePO(poId), 'Order closed')}>
                <XCircle size={14} />Close order
              </button>
            )}
            {receipts.length > 0 && (
              <Link className="ci-btn ci-btn-ghost" to={`/purchase/vendor-returns/new?po_id=${poId}&receipt_id=${receipts[0].receipt_id}`}><Undo2 size={14} />Return goods</Link>
            )}
            {canSubmit && (
              <button type="button" className="ci-btn ci-btn-primary" disabled={!!busy} onClick={() => run('submit', () => submitPOForApproval(poId, 'Submitted from the order page'), (r) => (String(r?.approval_status || '').toLowerCase() === 'pending' ? 'Sent for approval' : 'No approval needed'))}>
                <Send size={14} />Send for approval
              </button>
            )}
            {canReceive && (
              <Link className="ci-btn ci-btn-primary" to={`/purchase/receipts/new?po_id=${poId}`}><PackageCheck size={14} />Receive goods</Link>
            )}
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}
      {approval === 'pending' && (
        <p className="doc-note">This order is over your approval limit of {money(check?.threshold)} and is waiting for an admin to approve it. Goods can’t be received until then.</p>
      )}
      {approval === 'rejected' && <p className="doc-note">This order was rejected, so it can’t be received.</p>}

      {loading ? <Spinner label="Loading order…" /> : hdr && (
        <>
          <section className="doc-card">
            <div className="doc-card-head">
              <h2>Items ordered</h2>
              <span>{lines.length} {lines.length === 1 ? 'item' : 'items'}</span>
            </div>
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead>
                  <tr><th>Item</th><th className="num">Ordered</th><th className="num">Still to come</th><th className="num">Unit cost</th><th className="num">Amount</th></tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const rem = remaining.find((r) => String(r.product_id) === String(l.product_id))
                    return (
                      <tr key={`${l.product_id}-${i}`}>
                        <td>{l.name || l.sku}<MaterialTag kind={materialIds.has(Number(l.product_id)) ? 'material' : ''} /><span className="cell-sub">{l.sku}</span></td>
                        <td className="num">{num(l.quantity).toLocaleString()}</td>
                        <td className="num">{rem ? (num(rem.remaining) > 0 ? num(rem.remaining).toLocaleString() : 'All received') : '–'}</td>
                        <td className="num">{money(l.unit_cost)}</td>
                        <td className="num ci-total">{money(num(l.quantity) * num(l.unit_cost))}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <dl className="doc-totals">
              {costs.length > 0 && (
                <>
                  <div><dt>Items</dt><dd>{money(totals?.line_items_total ?? lineTotal)}</dd></div>
                  {costs.map((c, i) => <div key={i}><dt>{c.cost_name}</dt><dd>{money(c.amount)}</dd></div>)}
                </>
              )}
              <div className="is-total"><dt>Order total</dt><dd>{money(totals?.grand_total ?? hdr.total_amount ?? lineTotal)}</dd></div>
            </dl>
          </section>

          <section className="doc-card">
            <div className="doc-card-head">
              <h2>Deliveries</h2>
              <span>{stillToCome.length ? `${stillToCome.length} ${stillToCome.length === 1 ? 'item' : 'items'} still to come` : (receipts.length ? 'Everything has arrived' : '')}</span>
            </div>
            {receipts.length ? (
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>Receipt</th><th>Received</th><th>Location</th></tr></thead>
                  <tbody>
                    {receipts.map((r) => (
                      <tr key={r.receipt_id}>
                        <td><Link className="ci-je-link" to={`/purchase/receipts/${r.receipt_id}`}>{docNo('receipt', r.receipt_no)}</Link></td>
                        <td>{fmtDay(r.receipt_date) || '–'}</td>
                        <td>{r.location_name || '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="doc-empty">Nothing has been received against this order yet.</p>
            )}
          </section>

          {roles.includes('admin') && (
            <p className="doc-hint">
              Approval status looks wrong?{' '}
              <button type="button" className="link-button" disabled={!!busy} onClick={() => run('normalize', () => normalizePOApproval(poId), 'Approval status rechecked')}>Recheck it</button>
            </p>
          )}
        </>
      )}
    </div>
  )
}
